import { useState, useEffect, useMemo } from 'react'
import { DocumentResolutions } from './assemblies/DocumentResolutions'
import { useParams, useNavigate, useSearch, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { Button } from './ui/button'
import { useDocument, useDocumentType, useDocumentTypes, useAsset, useAssets, useDeleteDocument, useDocumentWithTextContent, useUpdateDocument } from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { apiBaseUrl } from '../api/client'
import { useApi } from '../api/hooks'
import { $api } from '../api/queryClient'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { useCallback } from 'react'
import { mdiArrowLeft, mdiBrain, mdiDelete, mdiDownload, mdiChevronDown, mdiChevronRight, mdiPencil, mdiOpenInNew, mdiEmail, mdiContentCopy, mdiCheck, mdiClose, mdiGraphOutline, mdiFolderOutline, mdiForum, mdiCallSplit } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { PIIBadge } from './PIIBadge'
import { SignedFlagBadges } from './documents/SignedFlagBadges'
import { RestrictedBadge, RetentionBadge } from './data-protection/RetentionBadge'
import { DocumentDataProtection, DocumentRestrictionBanner } from './data-protection/DocumentDataProtection'
import { RequestSignatureDialog } from './signatures/RequestSignatureDialog'
import { SignatureRequestsPanel } from './signatures/SignatureRequestsPanel'
import { DocumentErasureBanner, DocumentPurgeAction } from './data-protection/Erasure'
import type { PIIStatus } from '../lib/pii'
import { VisibilityBadge } from './VisibilityBadge'
import { useCreateJob } from '../api/jobs'
import { useLiveSubscription } from '../contexts/LiveEventsContext'
import { DocumentProperties } from './DocumentProperties'
import { ExtractionManifestSummary } from './documents/ExtractionManifestSummary'
import { AssetResolutionTrace } from './documents/AssetResolutionTrace'
import { DocumentPendingApprovals } from './documents/DocumentPendingApprovals'
import { DocumentCreatedAssets } from './documents/DocumentCreatedAssets'
import { DocumentEntitySuggestions } from './documents/DocumentEntitySuggestions'
import { DocumentLinkedEntities } from './documents/DocumentLinkedEntities'
import { EntityTagsSection } from './tags/EntityTagsSection'
import { DocumentIssuesButton } from './documents/DocumentIssuesButton'
import { DocumentAddressMismatchBanner } from './documents/DocumentAddressMismatchBanner'
import { IssueSheet } from './issues/IssueSheet'
import { DocumentTypeProposalCard } from './DocumentTypeProposalCard'
import { ArchivedMarker, AssetName } from './ArchivedMarker'
import { DocumentArchiveAction } from './archive/DocumentArchiveAction'
import type { DocumentConversationRef, DocumentDuplicateRef } from '../api/schema'
import { DocumentSplitDialog, canSplitDocument } from './documents/DocumentSplitDialog'
import { DocumentBundleContents } from './documents/DocumentBundleContents'
import { DocumentChildBanner } from './documents/DocumentChildBanner'
import { DocumentDuplicatesBanner } from './documents/DocumentDuplicatesBanner'
import { DocumentConversationPrivateBanner } from './documents/DocumentConversationPrivateBanner'
import { DocumentPageHighlight } from './documents/DocumentPageHighlight'
import { decodeHighlightBoxes, decodeHighlightOrigin } from '../lib/documentPageHighlight'
import { DocumentPreview, getFileTypeIcon } from './documents/DocumentPreview'
import { threadTitleT } from './conversations/thread'
import { SECTION_ICONS } from '../lib/section-icons'

// DocumentType interface used for document types data structure
interface DocumentType {
    uid: string
    code: string
    name: Record<string, string> | string
}

function parseEmailFrom(from: string): { name: string; email: string } {
    const match = from.match(/(.*?)\s*<(.+?)>/)
    if (match) return { name: match[1].trim(), email: match[2] }
    return { name: from, email: from }
}

function EmailBanner({ properties, formatDate, t }: {
    properties: { from: string; to: string; subject: string; date: string }
    formatDate: (d: string | undefined) => string
    t: (key: string) => string
}) {
    const sender = parseEmailFrom(properties.from)
    return (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3" data-testid="email-banner">
            <div className="flex items-start gap-3">
                <AppIcon path={mdiEmail} size="md" className="text-primary mt-0.5 shrink-0" />
                <div className="min-w-0 space-y-1">
                    <div className="font-medium text-foreground truncate">{properties.subject}</div>
                    <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                        <span><span className="font-medium">{t('email.from')}:</span> {sender.name} <span className="text-muted-foreground/70">&lt;{sender.email}&gt;</span></span>
                        <span><span className="font-medium">{t('email.to')}:</span> {properties.to}</span>
                        <span><span className="font-medium">{t('email.date')}:</span> {formatDate(properties.date)}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

function CopyableCode({ code }: { code: string }) {
    const { t } = useTranslation('documents')
    const bracketed = `[${code}]`
    const [copied, setCopied] = useState(false)

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        if (typeof navigator !== 'undefined' && navigator.clipboard) {
            void navigator.clipboard.writeText(bracketed).then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
            })
        }
    }

    return (
        <button
            type="button"
            onClick={handleClick}
            data-testid="document-type-code-copy"
            aria-label={t('detail.copy_type_code', { code: bracketed })}
            title={t('detail.code_copy_hint')}
            className="inline-flex max-w-full items-center gap-1 font-mono text-xs border border-border rounded px-1.5 py-0.5 bg-muted/40 hover:bg-muted transition-colors cursor-pointer"
        >
            <span className="min-w-0 text-left wrap-anywhere">{bracketed}</span>
            <AppIcon path={copied ? mdiCheck : mdiContentCopy} size="xs" className="shrink-0" />
            {copied && (
                <span className="sr-only" data-testid="document-type-code-copied">
                    {t('detail.code_copied')}
                </span>
            )}
        </button>
    )
}

export function DocumentDetailPage() {
    const { t } = useTranslation('documents')
    const { org, docUid } = useParams({ from: '/$org/documents/$docUid' })
    const navigate = useNavigate()
    const goToDocuments = useCallback(
        () => navigate({ to: '/$org/documents', params: { org: org || 'demo' }, search: { asset_uid: undefined, document_type_uid: undefined, flags: undefined, limit: 20 } }),
        [navigate, org],
    )
    const search = useSearch({ strict: false }) as { issue?: string; page?: string | number; hl?: string; from?: string; review_split?: boolean | string }
    const issueParam = search.issue
    const closeIssueSheet = useCallback(() => {
        navigate({
            search: (prev: Record<string, unknown>) => {
                const next = { ...prev }
                delete (next as { issue?: string }).issue
                return next as never
            },
            replace: false,
        })
    }, [navigate])
    useAuth()

    // "Page view" mode (spec 2026-07-17-14 §2): a bookmarkable deep link
    // (?page=N&hl=x,y,w,h;...) from a source badge lands here and swaps the
    // default preview for the rendered page + highlight overlay. Kept as a
    // mode on this same detail page (not a new route) so the breadcrumb and
    // the no-Back-button rule stay intact.
    const pageViewPage = search.page !== undefined ? Number(search.page) : undefined
    const pageViewBoxes = useMemo(() => decodeHighlightBoxes(search.hl), [search.hl])
    const pageViewOrigin = useMemo(() => decodeHighlightOrigin(search.from), [search.from])

    // Clicking the highlight box itself (spec: "we should be able to click on
    // the blinking box to go back to the matching asset and property") — only
    // wired up when the deep link actually carried a `from` origin; a
    // cold-opened or pasted link (no origin) leaves the box inert, same as
    // before. Lands on the entity's default/relevant tab; when the origin also
    // carries a property/clause id, `focusProperty`/`focusClause` tells that
    // page to scroll the row into view and pulse it (useFocusAndPulseRow) —
    // omitted entirely when the badge that built the link couldn't supply one.
    const activateHighlightOrigin = useCallback(() => {
        if (!pageViewOrigin) return
        if (pageViewOrigin.kind === 'asset') {
            // `tab: 'properties'` explicitly — not every asset type's default
            // tab is Properties (equipment-descended types default to
            // Equipment, sites/buildings/units to Data), so leaving it out
            // stranded a property-focused link on the wrong tab entirely,
            // never finding the row to scroll to.
            navigate({
                to: '/$org/assets/$assetUid',
                params: { org: org || 'demo', assetUid: pageViewOrigin.assetUid },
                search: {
                    tab: 'properties',
                    ...(pageViewOrigin.propertyUid ? { focusProperty: pageViewOrigin.propertyUid } : {}),
                } as never,
            })
            return
        }
        navigate({
            to: '/$org/contracts/$contractUid',
            params: { org: org || 'demo', contractUid: pageViewOrigin.contractUid },
            search: {
                tab: 'clauses',
                ...(pageViewOrigin.clauseCode ? { focusClause: pageViewOrigin.clauseCode } : {}),
            } as never,
        })
    }, [navigate, org, pageViewOrigin])
    const exitPageView = useCallback(() => {
        navigate({
            search: (prev: Record<string, unknown>) => {
                const next = { ...prev }
                delete (next as { page?: string; hl?: string }).page
                delete (next as { page?: string; hl?: string }).hl
                return next as never
            },
            replace: false,
        })
    }, [navigate])

    // Leaving the page view by clicking beside the highlight — the way
    // clicking off a lightbox closes it. It drops `page`/`hl` and shows the
    // document normally: exactly what the toolbar's "Exit page view" does,
    // reachable without scrolling back up to find the button.
    //
    // Deliberately NOT a history back(), though "the page I came from" is the
    // tempting reading. There is no reliable way to tell whether there IS an
    // in-app page behind this one: measured on a cold open (a pasted link, a
    // new tab) versus arriving from a clause link, `useCanGoBack()` is true in
    // both, `window.history.length` is 3 in both (the auth redirect pushes its
    // own entries), and the router's `__TSR_index` is 1 in both. Going back
    // from a cold open lands on the login redirect and bounces the reader out
    // of the document entirely. The browser's own Back button still does the
    // history thing, and it is right far more often than this guess would be.
    const leavePageView = exitPageView

    // Live entity notifications for this specific document — covers the
    // main record (useDocument below) and its properties block
    // (DocumentProperties, rendered further down) via
    // live-invalidation.ts's documents.detail path list. This is what lets
    // the OCR -> classification -> extraction pipeline's results appear
    // live instead of only via useDocument's ad-hoc LLM-fallback poll.
    useLiveSubscription({ entity: 'documents', uid: docUid })

    // React Query hooks
    const { data: documentData, isLoading: loading, error: queryError } = useDocument(org || 'demo', docUid)

    // Fetch specific document type and asset for display (efficient)
    const { data: documentType } = useDocumentType(
        org,
        documentData?.document_type_uid || ''
    )
    const { data: asset } = useAsset(
        org,
        documentData?.asset_uid || ''
    )

    // Fetch all document types and assets for dropdown options
    const { data: documentTypes = [] as DocumentType[] } = useDocumentTypes(org)
    const { data: assets = [] } = useAssets(org)
    const updateDocumentMutation = useUpdateDocument(org, docUid)

    // PII verdict (spec 2026-08-13-02 §4). `clear` re-derives the presumption
    // from the document type rather than meaning "no PII".
    const setPIIStatus = useCallback(
        (status: PIIStatus) => {
            void updateDocumentMutation.mutateAsync({ pii_status: status })
        },
        [updateDocumentMutation]
    )
    const deleteDocumentMutation = useDeleteDocument(org)
    const createJobMutation = useCreateJob(org)

    // API search handlers for ApiSearchableSelect
    const { request } = useApi()

    // The act a signature request carries (spec 2026-10-05-05): the document's
    // contract, else the one intervention it is attached to. Same query as the
    // "Linked to" card, so it reads from the same cache entry.
    const linksQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/documents/{documentUid}/links',
        { params: { path: { orgCode: org || 'demo', documentUid: docUid || '' } } },
        { staleTime: 5 * 60 * 1000, retry: 1, enabled: !!docUid },
    )
    // Bundle detection (spec 2026-10-14-15): a document in the « découpage à
    // valider » queue carries the split_proposal flag; its proposal prefills
    // the split dialog.
    const hasSplitProposal = !!documentData?.flags?.includes('split_proposal')
    const splitProposalQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/documents/{documentUid}/split-proposal',
        { params: { path: { orgCode: org || 'demo', documentUid: docUid || '' } } },
        { retry: false, enabled: !!docUid && hasSplitProposal },
    )
    const splitProposal = hasSplitProposal ? splitProposalQuery.data : undefined
    const [splitOpenRequest, setSplitOpenRequest] = useState(0)
    const signatureInterventionUid = useMemo(() => {
        const links = ((linksQuery.data as { data?: { entity_type: string; entity_uid: string }[] } | undefined)?.data) ?? []
        const ivs = [...new Set(links.filter((l) => l.entity_type === 'intervention').map((l) => l.entity_uid))]
        return ivs.length === 1 ? ivs[0] : undefined
    }, [linksQuery.data])
    
    const searchDocumentTypes = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        // For document types, we do client-side filtering since we load all types upfront
        let options = documentTypes.map((type: any) => ({
            value: type.uid,
            label: typeof type.name === 'string' ? type.name : type.name?.['fr'] || type.name?.['en'] || type.code || 'Unknown'
        }))
        
        if (query && query.length >= 2) {
            const lowerQuery = query.toLowerCase()
            options = options.filter((option: ApiSearchableSelectOption) => 
                option.label.toLowerCase().includes(lowerQuery)
            )
        }
        
        return options
    }, [documentTypes])

    const searchAssets = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        try {
            const queryParams: Record<string, string> = {
                limit: '50'
            }

            if (query) {
                queryParams.q = query
                queryParams.sort = 'name'
            } else {
                queryParams.sort = '-updated_at'
            }

            const response = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
                params: {
                    path: { orgCode: org },
                    query: queryParams
                }
            })

            const assets = (response as any)?.data || []

            return assets.map((asset: any) => ({
                value: asset.uid,
                label: typeof asset.name === 'string' ? asset.name : asset.name?.['fr'] || asset.name?.['en'] || 'Unknown'
            }))
        } catch (error) {
            console.error('Asset search error:', error)
            return []
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org])

    // Individual field edit states
    const [editingCodeField, setEditingCodeField] = useState(false)
    const [editingNameField, setEditingNameField] = useState(false)
    const [editingDocumentTypeField, setEditingDocumentTypeField] = useState(false)
    const [editingAssetField, setEditingAssetField] = useState(false)
    const [editingCode, setEditingCode] = useState('')
    const [editingName, setEditingName] = useState('')
    const [editingDocumentType, setEditingDocumentType] = useState('')
    const [editingAsset, setEditingAsset] = useState('')
    const [editingAssetName, setEditingAssetName] = useState('')

    // Text content panel state
    const [isTextContentExpanded, setIsTextContentExpanded] = useState(false)
    const { data: documentWithTextContent, isLoading: textContentLoading } = useDocumentWithTextContent(
        org,
        docUid,
        isTextContentExpanded
    )

    const error = (queryError as any)?.message || null

    // Initialize editing values when document data loads
    useEffect(() => {
        if (documentData) {
            setEditingCode((documentData as any).code || '')
            setEditingName(documentData.name || '')
            setEditingDocumentType(documentData.document_type_uid || '')
            setEditingAsset(documentData.asset_uid || '')
            setEditingAssetName(getAssetName(documentData.asset_uid || undefined))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentData])

    // Set breadcrumbs when document data is available
    useBreadcrumbs(
        documentData ? [
            { title: t('common:nav.home'), path: `/${org}` },
            { title: t('breadcrumb.documents'), path: `/${org}/documents` },
            {
                // A purged tombstone's name is a sentinel, shown through i18n.
                title: (documentData as { erasure_state?: string }).erasure_state === 'purged'
                    ? t('detail.purged_name')
                    : documentData.name || documentData.file_name || t('detail.title'),
            }
        ] : []
    )

    const handleDownload = () => {
        if (!documentData) return

        const downloadUrl = `${apiBaseUrl}/api/v1/organizations/${org || 'demo'}/documents/${docUid}/content?download=true`
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = documentData.name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const handleDelete = async () => {
        if (!documentData) return
        if (!confirm(t('detail.confirm_delete'))) return

        try {
            await deleteDocumentMutation.mutateAsync(docUid)
            // Navigate back to documents list
            navigate({ to: '/$org/documents', params: { org: org || 'demo' }, search: { asset_uid: undefined, document_type_uid: undefined, flags: undefined, limit: 20 } })
        } catch {
            // Error is handled by React Query
        }
    }

    type EditableField = 'code' | 'name' | 'documentType' | 'asset'

    const handleSaveField = async (field: EditableField, _value: string) => {
        if (!documentData) return

        const setEditingField = field === 'code' ? setEditingCodeField
            : field === 'name' ? setEditingNameField
                : field === 'documentType' ? setEditingDocumentTypeField
                    : setEditingAssetField

        try {
            const body: Record<string, unknown> = field === 'code'
                ? { code: _value || null }
                : field === 'name'
                    ? { name: _value }
                    : field === 'documentType'
                        ? { document_type_uid: _value || undefined }
                        : { asset_uid: _value || undefined }

            await updateDocumentMutation.mutateAsync(body)
            setEditingField(false)
        } catch {
            // Error is handled by React Query
        }
    }

    const handleCancelEdit = (field: EditableField) => {
        if (field === 'code') {
            setEditingCode((documentData as any)?.code || '')
            setEditingCodeField(false)
        } else if (field === 'name') {
            setEditingName(documentData?.name || '')
            setEditingNameField(false)
        } else if (field === 'documentType') {
            setEditingDocumentType(documentData?.document_type_uid || '')
            setEditingDocumentTypeField(false)
        } else {
            setEditingAsset(documentData?.asset_uid || '')
            setEditingAssetName(getAssetName(documentData?.asset_uid || undefined))
            setEditingAssetField(false)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent, field: EditableField, value: string) => {
        if (e.key === 'Enter') {
            handleSaveField(field, value)
        } else if (e.key === 'Escape') {
            handleCancelEdit(field)
        }
    }

    const startEditing = (field: EditableField) => {
        if (field === 'code') {
            setEditingCodeField(true)
        } else if (field === 'name') {
            setEditingNameField(true)
        } else if (field === 'documentType') {
            setEditingDocumentTypeField(true)
        } else {
            // For asset field, make sure we have the correct name when starting to edit
            setEditingAssetField(true)
            if (documentData?.asset_uid) {
                setEditingAssetName(getAssetName(documentData.asset_uid))
            }
        }
    }

    const formatFileSize = (bytes: number | undefined) => {
        if (!bytes) return t('list.unknown_size')
        const units = t('list.size_units', { returnObjects: true }) as string[]
        let size = bytes
        let unitIndex = 0
        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024
            unitIndex++
        }
        return `${size.toFixed(1)} ${units[unitIndex]}`
    }

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return t('list.unknown_size')
        return new Date(dateString).toLocaleString()
    }

    const getDocumentTypeName = (uid: string | undefined) => {
        if (!uid) return t('detail.no_document_type')

        // Use specific documentType if available (most efficient)
        if (documentType && documentType.uid === uid) {
            const name = documentType.name
            if (typeof name === 'object' && name) {
                return name.en || Object.values(name)[0] || documentType.code
            }
            return documentType.code
        }

        // Fallback to bulk data for dropdowns
        if (!documentTypes || !Array.isArray(documentTypes)) return uid
        const docType = documentTypes.find(dt => dt.uid === uid)
        if (!docType) return uid

        const name = docType.name
        if (typeof name === 'object' && name) {
            return name.en || Object.values(name)[0] || docType.code
        }
        return docType.code
    }

    const getAssetName = (uid: string | undefined) => {
        if (!uid) return t('detail.no_asset')

        // Use specific asset if available (most efficient)
        if (asset && asset.uid === uid) {
            return asset.name || asset.code || t('list.unknown_asset')
        }
        // Fallback to bulk data for dropdowns
        if (assets && Array.isArray(assets)) {
            const foundAsset = assets.find(a => a.uid === uid)
            if (foundAsset) {
                return foundAsset.name || foundAsset.code || t('list.unknown_asset')
            }
        }

        // Final fallback - never return UID, show a user-friendly message
        return t('list.unknown_asset')
    }

    const renderDocumentContent = () => {
        if (!documentData || !documentData.file_mime_type) return null

        // Page-view mode wins over the default preview whenever a `page`
        // deep link is present — this is what a source badge's
        // "view-source-in-document" action lands on.
        if (pageViewPage !== undefined && !Number.isNaN(pageViewPage)) {
            return (
                <div className="mt-6">
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-foreground">
                            {t('page_highlight.showing_page', { page: pageViewPage })}
                        </h3>
                        <Button
                            onClick={exitPageView}
                            variant="outline"
                            size="sm"
                            aria-label={t('page_highlight.exit_page_view')}
                            title={t('page_highlight.exit_page_view')}
                            data-testid="exit-page-view-btn"
                        >
                            <AppIcon path={mdiClose} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('page_highlight.exit_page_view')}</span>
                        </Button>
                    </div>
                    <DocumentPageHighlight
                        org={org || 'demo'}
                        docUid={docUid}
                        page={pageViewPage}
                        boxes={pageViewBoxes}
                        onDismiss={leavePageView}
                        onHighlightActivate={pageViewOrigin ? activateHighlightOrigin : undefined}
                    />
                </div>
            )
        }

        const downloadUrl = `${apiBaseUrl}/api/v1/organizations/${org || 'demo'}/documents/${docUid}/content`
        // No `size` param: the negotiated full-size rendition endpoint —
        // browser-safe types stream as-is, HEIC/HEIF/TIFF get transcoded to
        // JPEG server-side (spec 2026-09-13-06).
        const previewUrl = `${apiBaseUrl}/api/v1/organizations/${org || 'demo'}/documents/${docUid}/thumb`

        return (
            <div className="mt-6">
                <h3 className="text-lg font-semibold text-foreground mb-3">{t('detail.preview')}</h3>
                <DocumentPreview
                    mimeType={documentData.file_mime_type}
                    name={documentData.name}
                    contentUrl={downloadUrl}
                    previewUrl={previewUrl}
                    maxHeight={600}
                />
            </div>
        )
    }

    const renderTextContent = () => {
        if (!isTextContentExpanded) return null

        if (textContentLoading) {
            return (
                <div className="p-4 bg-muted/50 border-t border-border">
                    <p className="text-muted-foreground">{t('text_content.loading')}</p>
                </div>
            )
        }

        const textContent = documentWithTextContent?.text_content
        if (!textContent) {
            return (
                <div className="p-4 bg-muted/50 border-t border-border">
                    <p className="text-muted-foreground">{t('text_content.empty')}</p>
                </div>
            )
        }

        return (
            <div className="p-4 bg-muted/50 border-t border-border">
                <div className="text-sm text-foreground whitespace-pre-wrap font-mono">
                    {textContent.split('\n').map((line, index) => (
                        <span key={index}>
                            {line}
                            {index < textContent.split('\n').length - 1 && <br />}
                        </span>
                    ))}
                </div>
            </div>
        )
    }

    const renderTextContentPanel = () => {
        return (
            <div className="mt-6 bg-card border border-border rounded-lg">
                <div
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setIsTextContentExpanded(!isTextContentExpanded)}
                >
                    <div className="p-4 flex items-center justify-between">
                        <h3 className="text-sm font-medium text-foreground">{t('text_content.title')}</h3>
                        <AppIcon tone="muted" path={isTextContentExpanded ? mdiChevronDown : mdiChevronRight} size="md" />
                    </div>
                </div>
                {renderTextContent()}
            </div>
        )
    }

    // Memoized options for dropdown fields
    const documentTypeOptions = useMemo(() => {
        return documentTypes.map(type => ({
            value: type.uid,
            label: typeof type.name === 'string' ? type.name : type.name?.['fr'] || type.name?.['en'] || type.code || 'Unknown'
        }))
    }, [documentTypes])

    const assetInitialOptions = useMemo(() => {
        return editingAsset && editingAssetName ? [{ value: editingAsset, label: editingAssetName }] : []
    }, [editingAsset, editingAssetName])

    // Value change handler for asset field
    const handleAssetValueChange = useCallback((value: string) => {
        setEditingAsset(value)
        // For direct value changes (like from onValueChange), try to get name from cache
        if (value) {
            const existingName = getAssetName(value)
            if (existingName && existingName !== 'No asset' && existingName !== 'Unknown Asset') {
                setEditingAssetName(existingName)
            } else {
                setEditingAssetName('Unknown Asset')
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Option select handler for asset field (when user selects from dropdown)
    const handleAssetOptionSelect = useCallback((option: { value: string; label: string }) => {
        setEditingAsset(option.value)
        setEditingAssetName(option.label)
    }, [])

    const renderEditableDropdownField = (
        label: string,
        field: 'documentType' | 'asset',
        editingState: boolean,
        editingValue: string,
        setEditingValue: (value: string) => void,
        displayValue: string | React.ReactNode
    ) => {
        const savingState = updateDocumentMutation.isPending
        const searchFunction = field === 'documentType' ? searchDocumentTypes : searchAssets
        const initialOptions = field === 'documentType' ? documentTypeOptions : assetInitialOptions
        const handleValueChange = field === 'asset' ? handleAssetValueChange : setEditingValue
        const placeholder = field === 'documentType' ? t('detail.select_type_placeholder') : t('detail.select_asset_placeholder')
        const searchPlaceholder = field === 'documentType' ? t('detail.search_types') : t('detail.search_assets')
        const emptyMessage = field === 'documentType' ? t('detail.no_types_found') : t('detail.no_assets_found')

        return (
            <div>
                <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
                {editingState ? (
                    <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1">
                            <ApiSearchableSelect
                                value={editingValue}
                                placeholder={placeholder}
                                searchPlaceholder={searchPlaceholder}
                                emptyMessage={emptyMessage}
                                onSearch={searchFunction}
                                initialOptions={initialOptions}
                                onValueChange={handleValueChange}
                                onOptionSelect={field === 'asset' ? handleAssetOptionSelect : undefined}
                                disabled={savingState}
                                minSearchLength={field === 'asset' ? 2 : 0}
                                debounceMs={150}
                            />
                        </div>
                        <Button
                            onClick={() => handleSaveField(field, editingValue)}
                            disabled={savingState}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1 text-sm"
                        >
                            {savingState ? '...' : '✓'}
                        </Button>
                        <Button
                            onClick={() => handleCancelEdit(field)}
                            disabled={savingState}
                            variant="outline"
                            className="px-3 py-1 text-sm"
                        >
                            ✕
                        </Button>
                    </div>
                ) : (
                    <dd
                        className="text-sm text-foreground cursor-pointer hover:bg-muted/50 p-2 rounded transition-colors"
                        onClick={() => startEditing(field)}
                        title={t('detail.click_to_edit')}
                    >
                        {displayValue || t('detail.click_to_select')}
                    </dd>
                )}
            </div>
        )
    }

    const renderAssetField = () => {
        const assetUid = documentData?.asset_uid
        const assetName = getAssetName(assetUid || undefined)
        const hasAsset = assetUid && assetName !== t('detail.no_asset') && assetName !== t('list.unknown_asset')

        return (
            <div>
                <dt className="text-sm font-medium text-muted-foreground">{t('detail.asset')}</dt>
                {editingAssetField ? (
                    <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1">
                            <ApiSearchableSelect
                                value={editingAsset}
                                placeholder={t('detail.select_asset_placeholder')}
                                searchPlaceholder={t('detail.search_assets')}
                                emptyMessage={t('detail.no_assets_found')}
                                onSearch={searchAssets}
                                initialOptions={assetInitialOptions}
                                onValueChange={handleAssetValueChange}
                                onOptionSelect={handleAssetOptionSelect}
                                disabled={updateDocumentMutation.isPending}
                                minSearchLength={2}
                                debounceMs={150}
                            />
                        </div>
                        <Button
                            onClick={() => handleSaveField('asset', editingAsset)}
                            disabled={updateDocumentMutation.isPending}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1 text-sm"
                        >
                            {updateDocumentMutation.isPending ? '...' : '✓'}
                        </Button>
                        <Button
                            onClick={() => handleCancelEdit('asset')}
                            disabled={updateDocumentMutation.isPending}
                            variant="outline"
                            className="px-3 py-1 text-sm"
                        >
                            ✕
                        </Button>
                    </div>
                ) : (
                    <dd className="text-sm text-foreground p-2 rounded transition-colors flex items-center justify-between group">
                        {hasAsset ? (
                            <button
                                onClick={() => navigate({ to: '/$org/assets/$assetUid', params: { org: org || 'demo', assetUid: assetUid! } })}
                                className="flex items-center gap-2 hover:text-primary transition-colors"
                                title={t('detail.view_asset')}
                            >
                                <AssetName name={assetName} archivedAt={asset?.uid === assetUid ? asset?.archived_at : undefined} />
                                <AppIcon path={mdiOpenInNew} size="xs" className="opacity-70" />
                            </button>
                        ) : (
                            <span className="text-muted-foreground">{t('detail.no_asset')}</span>
                        )}
                        <Button
                            onClick={() => startEditing('asset')}
                            variant="ghost"
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 h-auto"
                            title={t('detail.edit_asset')}
                        >
                            <AppIcon path={mdiPencil} size="sm" />
                        </Button>
                    </dd>
                )}
            </div>
        )
    }

    /**
     * The document's CONTRACT home (spec 2026-08-13-01).
     *
     * A document lives on an asset and/or a contract, and a contract-only
     * document — a tenant's payslip, say — has no asset at all. Without this
     * field it would read as an orphan on its own detail page: the asset field
     * says "no asset" and the contract is no longer a document_link, so
     * DocumentLinkedEntities shows nothing either.
     *
     * Rendered ALONGSIDE the asset field (never instead of it: both homes can
     * be set) and only when there is a contract, so documents that have none
     * don't grow a permanently empty row. Read-only here — re-filing happens
     * from the contract's own documents tab.
     */
    const renderContractField = () => {
        const contractUid = (documentData as { contract_uid?: string | null } | undefined)?.contract_uid
        if (!contractUid) return null
        const contractName = (documentData as { contract_name?: string | null } | undefined)?.contract_name

        return (
            <div data-testid="document-contract-field">
                <dt className="text-sm font-medium text-muted-foreground">{t('detail.contract')}</dt>
                <dd className="text-sm text-foreground p-2 rounded transition-colors">
                    <button
                        onClick={() => navigate({ to: '/$org/contracts/$contractUid', params: { org: org || 'demo', contractUid } })}
                        className="flex items-center gap-2 hover:text-primary transition-colors"
                        title={t('detail.view_contract')}
                        data-testid="document-contract-link"
                    >
                        <AppIcon path={SECTION_ICONS.contracts} size="xs" className="opacity-70" />
                        <span>{contractName || t('detail.contract')}</span>
                        <AppIcon path={mdiOpenInNew} size="xs" className="opacity-70" />
                    </button>
                </dd>
            </div>
        )
    }

    /**
     * Original deposit path (spec 2026-08-16-04 §5) — where this file lived in
     * the depositor's own tree. Frozen provenance: read-only, no edit
     * affordance, and the row is absent entirely for documents that were not
     * deposited with a path. No tree navigation here — that is spec 07.
     */
    const renderOriginalPathField = () => {
        const sourceDetails = (documentData as { source_details?: { original_path?: string } | null } | undefined)?.source_details
        const originalPath = sourceDetails?.original_path
        if (!originalPath) return null

        return (
            // A path is long-form text, so it takes a row of its own instead of
            // being squeezed into one column of the metadata grid.
            <div className="col-span-full" data-testid="document-original-path-field">
                <dt className="text-sm font-medium text-muted-foreground">{t('detail.original_path')}</dt>
                <dd
                    className="text-sm text-foreground flex items-start gap-2 p-2"
                    title={t('detail.original_path_hint')}
                >
                    <AppIcon path={mdiFolderOutline} size="xs" className="opacity-70 mt-0.5 shrink-0" />
                    <span className="font-mono text-xs break-all" data-testid="document-original-path-value">
                        {originalPath}
                    </span>
                </dd>
            </div>
        )
    }

    const renderEditableField = (
        label: string,
        field: 'code' | 'name',
        value: string,
        editingState: boolean,
        editingValue: string,
        setEditingValue: (value: string) => void
    ) => {
        const savingState = updateDocumentMutation.isPending
        return (
            <div>
                <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
                {editingState ? (
                    <div className="flex items-center gap-2 mt-1">
                        <input
                            type="text"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, field, editingValue)}
                            className="flex-1 px-3 py-2 border border-border bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring text-sm"
                            disabled={savingState}
                            autoFocus
                        />
                        <Button
                            onClick={() => handleSaveField(field, editingValue)}
                            disabled={savingState}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1 text-sm"
                        >
                            {savingState ? '...' : '✓'}
                        </Button>
                        <Button
                            onClick={() => handleCancelEdit(field)}
                            disabled={savingState}
                            variant="outline"
                            className="px-3 py-1 text-sm"
                        >
                            ✕
                        </Button>
                    </div>
                ) : (
                    <dd
                        className="text-sm text-foreground cursor-pointer hover:bg-muted/50 p-2 rounded transition-colors"
                        onClick={() => startEditing(field)}
                        title={t('detail.click_to_edit')}
                    >
                        {value || t('detail.click_to_add')}
                    </dd>
                )}
            </div>
        )
    }

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-center py-12">
                    <div className="text-lg text-muted-foreground">{t('detail.loading')}</div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-foreground">{t('detail.title')}</h1>
                    <Button
                        onClick={() => navigate({ to: '/$org/documents', params: { org: org || 'demo' }, search: { asset_uid: undefined, document_type_uid: undefined, flags: undefined, limit: 20 } })}
                        variant="outline"
                        className="p-2"
                        title={t('detail.back')}
                    >
                        <AppIcon path={mdiArrowLeft} size="md" />
                    </Button>
                </div>

                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-destructive" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-destructive">{t('detail.error_loading')}</h3>
                            <p className="text-sm text-destructive mt-1">{error}</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // A purged document answers its tombstone (spec 2026-10-03-02, lot 4).
    const purgedAt = (documentData as { purged_at?: string } | undefined)?.purged_at

    if (!documentData || purgedAt) {
        return (
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-foreground">{t('detail.title')}</h1>
                    <Button
                        onClick={() => navigate({ to: '/$org/documents', params: { org: org || 'demo' }, search: { asset_uid: undefined, document_type_uid: undefined, flags: undefined, limit: 20 } })}
                        variant="outline"
                        className="p-2"
                        title={t('detail.back')}
                    >
                        <AppIcon path={mdiArrowLeft} size="md" />
                    </Button>
                </div>

                <div className="text-center py-12" data-testid={purgedAt ? 'document-purged' : 'document-not-found'}>
                    <p className="text-muted-foreground">
                        {purgedAt ? t('detail.purged', { date: formatDate(purgedAt) }) : t('detail.not_found')}
                    </p>
                </div>
            </div>
        )
    }

    // GDPR purge under way (spec 2026-10-03-02, lot 4): banner instead of the Purge action.
    const erasureState = (documentData as { erasure_state?: string }).erasure_state

    return (
        <div className="space-y-6">
            <IssueSheet issueUid={issueParam ?? null} onClose={closeIssueSheet} />
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
                            <AppIcon path={getFileTypeIcon(documentData.file_mime_type)} size="md" />
                            {documentData.name || documentData.file_name || 'Unnamed Document'}
                        </h1>
                        <EntityTagsSection
                            org={org || 'demo'}
                            entityType="document"
                            entityUid={docUid}
                            className="flex flex-wrap items-center gap-1"
                            collapse={{ below: 'md', max: 2 }}
                        />
                        {/* Tri-state PII badge + verdict control (spec 2026-08-13-02 §8).
                            Reads the document's own `pii:*` token; the menu writes
                            `pii_status`, which is sticky against later retypes. */}
                        <PIIBadge
                            flags={(documentData as { flags?: string[] }).flags}
                            onSetStatus={setPIIStatus}
                            testId="document-pii-badge"
                        />
                        <SignedFlagBadges flags={(documentData as { flags?: string[] }).flags} testidPrefix="document" />
                        {/* GDPR retention and restriction (spec 2026-10-03-02). */}
                        <RetentionBadge
                            state={(documentData as { retention_state?: string }).retention_state}
                            until={(documentData as { retention_until?: string }).retention_until}
                            testId="document-retention-badge"
                        />
                        {(documentData as { restricted?: boolean }).restricted && (
                            <RestrictedBadge testId="document-restricted-badge" />
                        )}
                        {/* Audience (spec 2026-09-04-03, decision 7). Always
                            readable — knowing whether a document is shared with
                            residents is part of understanding it — and only the
                            toggle is gated on `documents.write` on THIS row. */}
                        <VisibilityBadge
                            visibility={
                                (documentData as { effective_visibility?: string })
                                    .effective_visibility
                            }
                            writePermission="documents.write"
                            entity={documentData as { permissions?: string[] }}
                            onChange={(next) => {
                                void updateDocumentMutation.mutateAsync({
                                    visibility: next,
                                } as never)
                            }}
                            testId="document-visibility-badge"
                        />
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <span>{formatFileSize(documentData.file_size)}</span>
                    </div>
                </div>
                <div className="flex gap-2">
                    {docUid && (
                        <Link
                            to="/$org/graph"
                            params={{ org: org || 'demo' }}
                            search={{ focus: `doc@${docUid}`, expanded: undefined }}
                        >
                            <Button
                                variant="outline"
                                className="p-2"
                                data-testid="view-in-graph-btn"
                                aria-label={t('graph:action.view_in_graph')}
                                title={t('graph:action.view_in_graph')}
                            >
                                <AppIcon path={mdiGraphOutline} size="md" />
                            </Button>
                        </Link>
                    )}
                    <DocumentIssuesButton org={org || 'demo'} docUid={docUid} />
                    {canSplitDocument(documentData as Parameters<typeof canSplitDocument>[0]) && (
                        <DocumentSplitDialog
                            org={org || 'demo'}
                            documentUid={docUid}
                            proposal={splitProposal}
                            autoOpen={search.review_split === true || search.review_split === 'true'}
                            openRequest={splitOpenRequest}
                        />
                    )}
                    {documentData.file_mime_type === 'application/pdf' && !erasureState && (
                        <RequestSignatureDialog
                            org={org || 'demo'}
                            documentUid={docUid}
                            documentName={documentData.name}
                            subject={(documentData as { contract_uid?: string | null }).contract_uid
                                ? { type: 'contract', uid: (documentData as { contract_uid?: string }).contract_uid as string }
                                : signatureInterventionUid
                                    ? { type: 'work_order', uid: signatureInterventionUid }
                                    : undefined}
                        />
                    )}
                    <Button
                        onClick={() => {
                            const jobType = documentData.document_type_uid ? 'doc_meta_extraction' : 'doc_classification'
                            createJobMutation.mutate({ type: jobType, properties: { document_uid: docUid } })
                        }}
                        variant="outline"
                        className="p-2"
                        disabled={createJobMutation.isPending}
                        title={t('detail.retrigger_ai')}
                        data-testid="retrigger-ai-btn"
                    >
                        <AppIcon path={mdiBrain} size="md" className={createJobMutation.isPending ? 'animate-pulse' : undefined} />
                    </Button>
                    <Button
                        onClick={handleDownload}
                        className="bg-primary hover:bg-primary/90 p-2"
                        title={t('detail.download')}
                    >
                        <AppIcon path={mdiDownload} size="md" />
                    </Button>
                    {!erasureState && (
                        <DocumentPurgeAction org={org || 'demo'} docUid={docUid} onPurged={goToDocuments} />
                    )}
                    <ArchivedMarker archivedAt={documentData.archived_at} testId="document-archived-marker" />
                    <DocumentArchiveAction org={org || 'demo'} docUid={docUid} archivedAt={documentData.archived_at} />
                    <Button
                        onClick={handleDelete}
                        variant="outline"
                        className="text-destructive hover:text-destructive/80 p-2"
                        title={t('detail.delete')}
                    >
                        <AppIcon path={mdiDelete} size="md" />
                    </Button>
                </div>
            </div>

            {/* Bundle child banner — links back to the parent bundle (spec 2026-07-17-06) */}
            {(documentData as any).parent_document_uid && (
                <DocumentChildBanner
                    org={org || 'demo'}
                    parentUid={(documentData as any).parent_document_uid}
                    parentName={(documentData as any).parent_document_name}
                    pages={(documentData as any).parent_document_pages}
                />
            )}

            {/* Byte-identical twins of this file (spec 2026-11-03-04) */}
            <DocumentDuplicatesBanner
                org={org || 'demo'}
                docUid={docUid}
                createdAt={documentData.created_at}
                archivedAt={documentData.archived_at}
                duplicates={(documentData as { duplicates?: DocumentDuplicateRef[] }).duplicates}
                document={documentData as { permissions?: string[] }}
                formatDate={formatDate}
            />

            {/* Address-mismatch warning (data_consistency issue on the filing asset) */}
            <DocumentAddressMismatchBanner
                org={org || 'demo'}
                docUid={docUid}
                assetUid={documentData.asset_uid}
            />

            {erasureState && <DocumentErasureBanner org={org || 'demo'} docUid={docUid} onPurged={goToDocuments} />}
            {(documentData as { restricted?: boolean }).restricted && <DocumentRestrictionBanner />}

            {/* Private to its conversation until a manager shares it with the
                organization (spec 2026-09-12-01 § 3). */}
            {((documentData as any).flags as string[] | undefined)?.includes('conversation_private') && (
                <DocumentConversationPrivateBanner
                    org={org || 'demo'}
                    docUid={docUid}
                    conversations={(documentData as any).conversations as DocumentConversationRef[] | undefined}
                    document={documentData as { permissions?: string[] }}
                />
            )}

            {/* The way back to the resident thread this file arrived on
                (spec 2026-09-08-07 § 2). One chip per thread — the same file
                can legitimately be attached to several. */}
            {((documentData as any).conversations as DocumentConversationRef[] | undefined)?.length ? (
                <div className="flex flex-wrap gap-2" data-testid="document-conversations">
                    {((documentData as any).conversations as DocumentConversationRef[]).map((conversation) => (
                        <Link
                            key={`${conversation.uid}-${conversation.message_uid}`}
                            to="/$org/conversations"
                            params={{ org: org || 'demo' }}
                            search={{
                                status: undefined,
                                channel: undefined,
                                resolution: undefined,
                                asset: undefined,
                                unattached: undefined,
                                draft: undefined,
                                q: undefined,
                                selected: undefined,
                                reply_for_issue: undefined,
                                conversation: String(conversation.uid),
                                // The exact message this file arrived on, when
                                // the reference names one — a deep link into
                                // the thread, not just to it.
                                message: conversation.message_uid ? String(conversation.message_uid) : undefined,
                            }}
                            className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs text-foreground hover:bg-primary/10"
                            data-testid={`document-conversation-${conversation.uid}`}
                        >
                            <AppIcon path={mdiForum} size="xs" className="shrink-0 text-primary" />
                            <span className="truncate max-w-[28rem]">
                                {t('conversations.from_conversation', {
                                    subject: threadTitleT(conversation, t, 'conversations.no_subject'),
                                })}
                            </span>
                        </Link>
                    ))}
                </div>
            ) : null}

            {/* Email Provenance Banner */}
            {((documentData as any).flags as string[] | undefined)?.includes('email') && (documentData.properties as any)?.email && (
                <EmailBanner
                    properties={(documentData.properties as any).email}
                    formatDate={formatDate}
                    t={t}
                />
            )}

            {/* AI document-type classification review (suggestion / proposal / processing) */}
            <DocumentTypeProposalCard
                org={org || 'demo'}
                docUid={docUid}
                document={documentData}
                documentTypeName={(uid) => getDocumentTypeName(uid)}
                onClassifyWithType={(typeUid) => handleSaveField('documentType', typeUid)}
                onChooseAnother={() => setEditingDocumentTypeField(true)}
            />

            {/* Document Info */}
            <div className="bg-card rounded-lg border border-border p-4">
                {/* Check if any field is being edited to determine layout */}
                {editingCodeField || editingNameField || editingDocumentTypeField || editingAssetField ? (
                    <dl className="space-y-4">
                        {renderEditableField(
                            t('detail.code'),
                            'code',
                            (documentData as any).code || '',
                            editingCodeField,
                            editingCode,
                            setEditingCode
                        )}
                        {renderEditableField(
                            t('detail.name'),
                            'name',
                            documentData.name || '',
                            editingNameField,
                            editingName,
                            setEditingName
                        )}
                        {renderEditableDropdownField(
                            t('detail.type'),
                            'documentType',
                            editingDocumentTypeField,
                            editingDocumentType,
                            setEditingDocumentType,
                            <span className="inline-flex items-center gap-2 flex-wrap">
                                <span>{getDocumentTypeName(documentData.document_type_uid)}</span>
                                {documentData.document_type_uid && documentType?.code && (
                                    <CopyableCode code={documentType.code} />
                                )}
                            </span>
                        )}
                        {renderAssetField()}
                        {renderContractField()}
                        <div>
                            <dt className="text-sm font-medium text-muted-foreground">{t('detail.created_at')}</dt>
                            <dd className="text-sm text-foreground">{formatDate(documentData.created_at)}</dd>
                        </div>
                        {renderOriginalPathField()}
                    </dl>
                ) : (
                    // min-w-0 lets a column shrink below its longest word, and
                    // wrap-anywhere breaks a file name or a type code that has no
                    // space: without both they spilled into the next field (#1700).
                    <dl
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 [&>div]:min-w-0 [&_dd]:wrap-anywhere"
                        data-testid="document-info-fields"
                    >
                        {renderEditableField(
                            t('detail.code'),
                            'code',
                            (documentData as any).code || '',
                            editingCodeField,
                            editingCode,
                            setEditingCode
                        )}
                        {renderEditableField(
                            t('detail.name'),
                            'name',
                            documentData.name || '',
                            editingNameField,
                            editingName,
                            setEditingName
                        )}
                        {renderEditableDropdownField(
                            t('detail.type'),
                            'documentType',
                            editingDocumentTypeField,
                            editingDocumentType,
                            setEditingDocumentType,
                            <span className="inline-flex items-center gap-2 flex-wrap">
                                <span>{getDocumentTypeName(documentData.document_type_uid)}</span>
                                {documentData.document_type_uid && documentType?.code && (
                                    <CopyableCode code={documentType.code} />
                                )}
                            </span>
                        )}
                        {renderAssetField()}
                        {renderContractField()}
                        <div>
                            <dt className="text-sm font-medium text-muted-foreground">{t('detail.created_at')}</dt>
                            <dd className="text-sm text-foreground">{formatDate(documentData.created_at)}</dd>
                        </div>
                        {renderOriginalPathField()}
                    </dl>
                )}
            </div>

            {/* Bundle contents — children of a MIXED parent (spec 2026-07-17-06),
                or the extracted files of a deposit archive (spec 2026-08-16-06) */}
            {(((documentData as any).child_documents && (documentData as any).child_documents.length > 0) ||
                ((documentData as any).flags as string[] | undefined)?.includes('archive')) && (
                <DocumentBundleContents
                    org={org || 'demo'}
                    children={(documentData as any).child_documents || []}
                    isArchive={((documentData as any).flags as string[] | undefined)?.includes('archive')}
                    totalCount={(documentData as any).child_document_count}
                    documentUid={docUid}
                />
            )}

            {/* The resolutions read from a PV: title, outcome and the page they
                come from, above the raw content (spec 2026-10-10-08 §10). */}
            {documentType?.code === 'CONDO_AGM_MINUTES' && (
                <DocumentResolutions
                    org={org || 'demo'}
                    documentUid={docUid}
                    onJumpToPage={(page) =>
                        navigate({
                            search: ((prev: Record<string, unknown>) => ({ ...prev, page: String(page) })) as never,
                            replace: false,
                        })
                    }
                />
            )}

            {/* Generic document↔entity links (spec 2026-07-17-01) */}
            <DocumentLinkedEntities org={org || 'demo'} documentUid={docUid} />

            {/* Signature requests on this PDF, or that produced it (spec 2026-10-05-05) */}
            <SignatureRequestsPanel org={org || 'demo'} filter={{ document_uid: docUid }} testId="document-signature-requests" />

            {/* Persons concerned and disclosures (spec 2026-10-03-02) */}
            <DocumentDataProtection org={org || 'demo'} docUid={docUid} />

            {/* Document Properties */}
            <DocumentProperties org={org || 'demo'} documentUid={docUid} />

            {/* Pending property approvals sourced from this document */}
            <DocumentPendingApprovals org={org || 'demo'} documentUid={docUid} />

            {/* Pending asset creations from this document */}
            <DocumentCreatedAssets org={org || 'demo'} documentUid={docUid} />

            {/* Waiting on the filing asset's validation (spec 2026-09-16-02 §4):
                doc_entity_extraction stopped at the hard gate, and nothing on
                this page used to say so. The flag disappears as soon as a run
                gets past the gate — which approving the asset triggers. */}
            {documentData?.processing_flags?.includes('entity_extraction_pending_validation') && (
                <div
                    className="border border-amber-300/70 dark:border-amber-700/40 rounded-md p-3 bg-amber-50/30 dark:bg-amber-900/10 text-sm"
                    data-testid="entity-extraction-pending-validation"
                >
                    {documentData.asset_uid && asset ? (
                        <>
                            {t('entity_extraction_pending_validation.prefix')}{' '}
                            <Link
                                to="/$org/assets/$assetUid"
                                params={{ org: org || 'demo', assetUid: documentData.asset_uid }}
                                className="underline underline-offset-2"
                                data-testid="entity-extraction-pending-validation-asset"
                            >
                                {asset.name}
                            </Link>
                        </>
                    ) : (
                        t('entity_extraction_pending_validation.generic')
                    )}
                </div>
            )}

            {/* Pipeline states that used to be silent (spec 2026-10-04-02 §2, §5). */}
            {documentData?.processing_flags?.includes('text_extraction_empty') && (
                <div
                    className="border border-amber-300/70 dark:border-amber-700/40 rounded-md p-3 bg-amber-50/30 dark:bg-amber-900/10 text-sm"
                    data-testid="text-extraction-empty"
                >
                    {t('text_extraction_empty')}
                </div>
            )}
            {documentData?.processing_flags?.includes('asset_extraction_target_conflict') && (
                <div
                    className="border border-amber-300/70 dark:border-amber-700/40 rounded-md p-3 bg-amber-50/30 dark:bg-amber-900/10 text-sm"
                    data-testid="asset-extraction-target-conflict"
                >
                    {t('asset_extraction_target_conflict')}
                </div>
            )}
            {splitProposal?.status === 'pending' && (
                <div
                    className="border border-primary/40 rounded-md p-3 bg-primary/5 text-sm flex flex-wrap items-center justify-between gap-2"
                    data-testid="split-proposal-notice"
                >
                    <span>{t('bundle.proposal_notice', { count: splitProposal.segments.length })}</span>
                    <Button
                        size="sm"
                        onClick={() => setSplitOpenRequest((n) => n + 1)}
                        data-testid="split-proposal-notice-review-btn"
                    >
                        <AppIcon path={mdiCallSplit} size="sm" className="mr-1" />
                        {t('bundle.review_action')}
                    </Button>
                </div>
            )}
            {/* Entity extraction gave up on a looping answer (spec 2026-10-14-13). */}
            {documentData?.processing_flags?.includes('entity_extraction_runaway') && (
                <div
                    className="border border-amber-300/70 dark:border-amber-700/40 rounded-md p-3 bg-amber-50/30 dark:bg-amber-900/10 text-sm"
                    data-testid="entity-extraction-runaway"
                >
                    {t('entity_extraction_runaway')}
                </div>
            )}

            {/* Pending entity suggestions (doc_entity_extraction, spec 2026-07-11-04) */}
            <DocumentEntitySuggestions org={org || 'demo'} documentUid={docUid} />

            {/* Asset resolution trace (doc_asset_association) */}
            <AssetResolutionTrace org={org || 'demo'} documentUid={docUid} />

            {/* AI extraction manifest (doc_asset_extraction) */}
            <ExtractionManifestSummary org={org || 'demo'} documentUid={docUid} />

            {/* Document Content */}
            {renderDocumentContent()}

            {/* Text Content Panel */}
            {renderTextContentPanel()}
        </div>
    )
} 